import type { SoundConfig, SoundPresetId } from "../types/timer-settings";

interface SoundPresetMeta {
  readonly id: SoundPresetId;
  readonly label: string;
}

export const SOUND_PRESETS: readonly SoundPresetMeta[] = [
  { id: "chime", label: "Carillon" },
  { id: "bell", label: "Cloche" },
  { id: "ding", label: "Ding" },
  { id: "electronic", label: "Electronique" },
  { id: "nature", label: "Nature" },
  { id: "silent", label: "Silencieux" },
] as const;

let sharedCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (sharedCtx && sharedCtx.state !== "closed") return sharedCtx;
  sharedCtx = new (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  )();
  return sharedCtx;
}

function playChime(ctx: AudioContext, volume: number): void {
  const notes = [660, 520, 440];
  const noteDuration = 0.25;
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = "sine";
    const startAt = ctx.currentTime + i * noteDuration;
    gain.gain.setValueAtTime(volume, startAt);
    gain.gain.exponentialRampToValueAtTime(0.01, startAt + noteDuration);
    osc.start(startAt);
    osc.stop(startAt + noteDuration);
  });
}

function playBell(ctx: AudioContext, volume: number): void {
  const harmonics = [440, 880, 1320];
  harmonics.forEach((freq) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = "sine";
    gain.gain.setValueAtTime(volume * (440 / freq), ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 1.2);
  });
}

function playDing(ctx: AudioContext, volume: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = 1200;
  osc.type = "sine";
  gain.gain.setValueAtTime(volume, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.4);
}

function playElectronic(ctx: AudioContext, volume: number): void {
  const freqs = [800, 600, 800, 600];
  const step = 0.15;
  freqs.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = "square";
    const startAt = ctx.currentTime + i * step;
    gain.gain.setValueAtTime(volume * 0.5, startAt);
    gain.gain.exponentialRampToValueAtTime(0.01, startAt + step);
    osc.start(startAt);
    osc.stop(startAt + step);
  });
}

function playNature(ctx: AudioContext, volume: number): void {
  const bufferSize = ctx.sampleRate * 1;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const bandpass = ctx.createBiquadFilter();
  bandpass.type = "bandpass";
  bandpass.frequency.value = 600;
  bandpass.Q.value = 1.5;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(volume * 0.6, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1);

  source.connect(bandpass);
  bandpass.connect(gain);
  gain.connect(ctx.destination);
  source.start(ctx.currentTime);
  source.stop(ctx.currentTime + 1);
}

const PRESET_PLAYERS: Record<SoundPresetId, (ctx: AudioContext, volume: number) => void> = {
  chime: playChime,
  bell: playBell,
  ding: playDing,
  electronic: playElectronic,
  nature: playNature,
  silent: () => { /* no-op */ },
};

export function playSound(config: SoundConfig): void {
  if (config.preset === "silent" || config.volume === 0) return;
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") void ctx.resume();
    const normalizedVolume = config.volume / 100;
    PRESET_PLAYERS[config.preset](ctx, normalizedVolume);
  } catch {
    // Silent fallback — Web Audio API may not be available
  }
}
